import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = { name: "", gstin: "" };

const Firms = () => {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadFirms = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/firms", { size: 200 });
      setFirms(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load firms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFirms();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (firm) => {
    setEditingId(firm.id);
    setFormData({ name: firm.name || "", gstin: firm.gstin || "" });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/v1/firms/${editingId}`, formData);
        toast.success("Firm updated");
      } else {
        await api.post("/api/v1/firms", formData);
        toast.success("Firm created");
      }
      setShowModal(false);
      loadFirms();
    } catch (error) {
      toast.error(error.message || "Failed to save firm");
    } finally {
      setSaving(false);
    }
  };

  const filteredFirms = firms.filter((firm) =>
    [firm.name, firm.gstin].some((value) => String(value || "").toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading firms...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        A Firm is the parent group that owns one or more distributor branches (e.g. "SLV Group" owning several city-wise
        distributor entities). Assign a distributor to a firm from its Profile screen.
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search firm name / GSTIN"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="primary" onClick={openCreate}>
          + New Firm
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>GSTIN</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFirms.length === 0 ? (
              <tr>
                <td colSpan={4}>No firms found.</td>
              </tr>
            ) : (
              filteredFirms.map((firm) => (
                <tr key={firm.id}>
                  <td>{firm.id}</td>
                  <td>{firm.name}</td>
                  <td>{firm.gstin || "-"}</td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(firm)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Firm" : "Create Firm"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>GSTIN</label>
                  <input name="gstin" value={formData.gstin} onChange={handleChange} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Firms;
